<template>
  <div class="amc-course-container">
    <!-- 顶部导航栏 -->
    <div class="amc-header">
      <div class="header-left">
        <!-- <i class="el-icon-close close-btn" @click="goBack"></i> -->
        <el-button class="back-btn" type="text" icon="el-icon-arrow-left" @click="goToSubjectSelect" />
        <h1 class="course-title">{{ courseCenterTitle }}</h1>
        <el-dropdown class="title-dropdown" trigger="click" @command="handleLevelChange">
          <div class="title-trigger">
            <i class="el-icon-caret-bottom title-caret" />
            <span class="level-label">{{ currentCourseName }}</span>
          </div>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item
              v-for="item in currentCourseList"
              :key="String(item.id || item.courseName || item.course_name || item.name)"
              :command="String(item.courseName || item.course_name || item.name || '').toLowerCase()"
            >
              {{ String(item.courseName || item.course_name || item.name || '') }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
      <div class="header-right">
        <el-dropdown class="avatar-container" trigger="click" :hide-on-click="false">
          <div class="avatar-wrapper">
            <img :src="avatarUrl" class="user-avatar" @error="handleAvatarError">
            <i class="el-icon-caret-bottom" />
          </div>
          <el-dropdown-menu slot="dropdown" class="user-dropdown">
            <el-dropdown-item>
              <div style="display: flex; align-items: center; justify-content: space-between; min-width: 140px; padding: 0 5px;">
                <span style="margin-right: 10px; color: #606266;">复习提醒</span>
                <el-switch
                  v-model="reviewReminderEnabled"
                  active-color="#13ce66"
                  inactive-color="#ff4949"
                  size="small"
                  @change="handleReminderSwitch">
                </el-switch>
              </div>
            </el-dropdown-item>
            <el-dropdown-item divided @click.native="openMemberDialog">
              <div style="display: flex; align-items: center; min-width: 140px; padding: 0 5px;">
                <i class="el-icon-medal" style="margin-right: 8px; color: #667eea;"></i>
                <span :style="'color:' + (isMember ? '#67c23a' : '#606266')">
                  {{ isMember ? '已开通会员' : '开通会员' }}
                </span>
              </div>
            </el-dropdown-item>
            <el-dropdown-item divided @click.native="goMyself">个人中心</el-dropdown-item>
            <el-dropdown-item divided @click.native="logout"><span style="display: block">Log Out</span></el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </div>

    <div class="amc-home-body">
      <div class="overview-card">
        <div class="overview-stats">
          <div class="overview-stat-item" @click="nextAccuracyPage">
            <div class="overview-stat-icon icon-blue">
              <i class="el-icon-check"></i>
            </div>
            <div class="overview-stat-value">
              <template v-if="accuracyPage === 0">{{ accuracy }}%</template>
              <template v-else-if="accuracyPage === 1">{{ accuracyByDays[1].accuracy }}%</template>
              <template v-else>{{ accuracyByQuestions[1].accuracy }}%</template>
            </div>
            <div class="overview-stat-label">
              <template v-if="accuracyPage === 0">正确率</template>
              <template v-else-if="accuracyPage === 1">近{{ accuracyByDays[1].label }}正确率</template>
              <template v-else>近{{ accuracyByQuestions[1].label }}正确率</template>
            </div>
          </div>
          <div class="overview-stat-item" @click="toggleProgressDisplay">
            <div class="overview-stat-icon icon-green">
              <i class="el-icon-data-analysis"></i>
            </div>
            <div class="overview-stat-value" :style="{ color: progressColor }">{{ showProgressPercent ? studyProgress + '%' : completed }}</div>
            <div class="overview-stat-label">{{ showProgressPercent ? '备考进度' : '已完成' }}</div>
          </div>
          <div class="overview-stat-item" @click="goToWrongQuestions">
            <div class="overview-stat-icon icon-red">
              <i class="el-icon-question"></i>
            </div>
            <div class="overview-stat-value">{{ unreviewedWrongCount }}</div>
            <div class="overview-stat-label">待复习错题</div>
          </div>
        </div>
        <div class="exam-countdown-line">
          <span class="countdown-text">距离考试还有</span>
          <span class="countdown-days">{{ daysLeft }}</span>
          <span class="countdown-text">天</span>
          <span class="countdown-date">({{ examDate }})</span>
        </div>
      </div>

      <div class="task-card">
        <div class="section-title-main">今日任务</div>
        <div class="task-row">
          <div class="task-left"><span class="task-emoji">📚</span><span>今日练习</span></div>
          <div class="task-right">{{ todayStats.done }}/{{ dailyTarget }}</div>
        </div>
        <div class="task-row">
          <div class="task-left"><span class="task-emoji">�</span><span>今日错题统计</span></div>
          <div class="task-right">错题 {{ todayStats.wrong }} 已复习 {{ todayStats.review }}</div>
        </div>
        <div class="task-row task-row-calendar">
          <div class="task-left"><span class="task-emoji">💡</span><span>天数统计</span></div>
          <div class="task-right">
            <el-popover
              placement="bottom"
              trigger="hover"
              width="420"
              popper-class="amc-month-calendar-popover"
            >
              <el-calendar :value="currentMonthDate" @input="handleCalendarInput">
                <template slot="dateCell" slot-scope="{ date, data }">
                  <div
                    class="amc-calendar-date"
                    :class="{ 'is-completed': isDateCompleted(data.day) }"
                  >
                    <span>{{ data.day.split('-')[2] }}</span>
                  </div>
                </template>
              </el-calendar>
              <div slot="reference" class="task-calendar-trigger">
                本月已完成 {{ finishedDaysThisMonth }} 天
              </div>
            </el-popover>
          </div>
        </div>
      </div>

      <div class="quick-entry-grid">
        <div class="quick-entry-card" @click="startQuickPractice">
          <div class="quick-entry-icon"><i class="el-icon-reading"></i></div>
          <div class="quick-entry-title">练习</div>
          <div class="quick-entry-subtitle">今日已做 {{ todayStats.done }} 题</div>
        </div>
        <div class="quick-entry-card" @click="goToWrongQuestions">
          <div class="quick-entry-icon"><i class="el-icon-refresh-right"></i></div>
          <div class="quick-entry-title">纠错</div>
          <div class="quick-entry-subtitle">立即查看</div>
        </div>
      </div>

      <div class="feature-panel feature-panel-collapsible expanded">
        <div class="feature-panel-toggle">
          <div class="feature-panel-title">拓展功能</div>
        </div>
        <div class="feature-menu-grid">
          <div class="feature-menu-item" @click="goToKnowledgePoints">
            <div class="feature-menu-icon bg-green"><i class="el-icon-reading"></i></div>
            <div class="feature-menu-text">知识点精练</div>
          </div>
          <div class="feature-menu-item" @click="goToCourseResources">
            <div class="feature-menu-icon bg-orange"><i class="el-icon-menu"></i></div>
            <div class="feature-menu-text">课程资料</div>
          </div>
          <div class="feature-menu-item" @click="goToConsecutiveChallenge">
            <div class="feature-menu-icon bg-red"><i class="el-icon-medal"></i></div>
            <div class="feature-menu-text">连对挑战</div>
          </div>
          <div class="feature-menu-item" @click="goToPastPapers">
            <div class="feature-menu-icon bg-blue"><i class="el-icon-document"></i></div>
            <div class="feature-menu-text">历年真题</div>
          </div>
          <div class="feature-menu-item" @click="goToMockTest">
            <div class="feature-menu-icon bg-orange"><i class="el-icon-tickets"></i></div>
            <div class="feature-menu-text">模拟测试</div>
          </div>
          <div class="feature-menu-item" @click="goToWrongQuestions">
            <div class="feature-menu-icon bg-red"><i class="el-icon-notebook-2"></i></div>
            <div class="feature-menu-text">错题集</div>
          </div>
          <div class="feature-menu-item" @click="goToKnowledgeBase">
            <div class="feature-menu-icon bg-blue"><i class="el-icon-connection"></i></div>
            <div class="feature-menu-text">知识库</div>
          </div>
          <div class="feature-menu-item" @click="goToPracticeRecord">
            <div class="feature-menu-icon bg-green"><i class="el-icon-time"></i></div>
            <div class="feature-menu-text">做题记录</div>
          </div>
          <div class="feature-menu-item" @click="goToLearningAnalytics">
            <div class="feature-menu-icon bg-cyan"><i class="el-icon-data-analysis"></i></div>
            <div class="feature-menu-text">学情分析</div>
          </div>
          <div v-if="showCodePracticeEntry" class="feature-menu-item" @click="goToGespCodePractice">
            <div class="feature-menu-icon bg-purple"><i class="el-icon-monitor"></i></div>
            <div class="feature-menu-text">编程题练习</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 购买弹窗 -->
    <amc-purchase-dialog
      :visible.sync="showPurchaseDialog"
      :show-close="true"
      :course-type="purchaseCourseType"
      :course-level="purchaseCourseLevel"
      :course-display-name="memberDisplayName"
      @purchased="handlePurchased"
      @close="handlePurchaseClose">
    </amc-purchase-dialog>

    <div v-if="analyticsExporting" class="analytics-export-overlay">
      <div class="analytics-export-dialog">
        <div class="analytics-export-spinner">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <div class="analytics-export-title">正在生成学情分析报告{{ analyticsExportDots }}</div>
        <div class="analytics-export-desc">系统正在整理学情数据并生成 PDF 文件，请稍候。</div>
      </div>
    </div>
  </div>
</template>

<script>
// 通用接口
import { countByRepoId as countQuestionsByRepoId, pagingByRepoId } from '@/api/courseQuestion'
import { countByRepoId as countRecordsByRepoId, getAccuracyByRepoId, accuracyByDays, accuracyByLastN, unreviewedWrongCount, countTodayWrong } from '@/api/userQuestionRecord'
import { studentSelectedCourse, getCourseCategories, getCoursesByCategoryId } from '@/api/course'
import { fetchPaging as fetchRepoList } from '@/api/repo'
import { getTokenInfo } from '@/utils/jwtUtils'
import {
  getPlansByUserId,
  getPlanByUserIdAndRepoId,
  updatePlan,
  setActivePlan,
  getSchemeByUserIdAndRepoId,
  createScheme,
  updateScheme,
  upsertTodayPlan,
  getProgressByDate,
  getProgressPaging,
  countCompletedDays
} from '@/api/studyPlan'
// 通用状态接口
import { getMaxChallengeCount, getDailyStreakDays } from '@/api/userStatus'
// AMC 专用接口（仅保留必要的）
import { joinByInviteCode, getClassById } from '@/api/amcCourseClass'
import AmcPurchaseDialog from '@/components/AmcPurchaseDialog.vue'
import { exportLearningAnalyticsPdf } from '@/api/learningAnalytics'
import { ensureEnabledFeatures, hasFeature, ensureFeatureStatusMap, isFeatureActive, getFeatureLimit } from '@/utils/feature'
import { getMyUserMembership } from '@/api/userMembership'
import { Buffer } from 'buffer';
export default {
  name: 'AMCCourse',
  components: {
    AmcPurchaseDialog
  },
  computed: {
    purchaseCourseType() {
      return this.getEffectiveCourseTypeCode() || 'AMC'
    },
    memberDisplayName() {
      const lvl = this.purchaseCourseLevel
      if (lvl) return lvl
      const ct = this.currentCourseType
      return String(ct?.name || '').trim() || this.purchaseCourseType || 'AMC'
    },
    purchaseCourseLevel() {
      if (this.isAmcMode()) {
        const v = (this.selectedAmcLevel || '').toString().trim().toLowerCase()
        if (v === 'amc10') return 'AMC10'
        if (v === 'amc12') return 'AMC12'
        return 'AMC8'
      }
      return String(this.selectedLevelDisplay || '').trim()
    },
    currentCourseType() {
      const list = Array.isArray(this.courseTypeList) ? this.courseTypeList : []
      const cur = String(this.selectedAmcLevel || '').trim().toLowerCase()
      if (!list.length) return null

      const qid = this.selectedCourseTypeId != null ? String(this.selectedCourseTypeId) : ''
      if (qid) {
        const hitById = list.find(ct => String(ct?.id) === qid)
        if (hitById) return hitById
      }

       const qcode = String(this.selectedCourseTypeCode || '').trim().toLowerCase()
       if (qcode) {
         const hitByCode = list.find(ct => {
           const code = String(ct?.code || ct?.name || '').trim().toLowerCase()
           return code === qcode || code.includes(qcode) || qcode.includes(code)
         })
         if (hitByCode) return hitByCode
       }

      if (cur) {
        const hit = list.find(ct => {
          const cid = ct && ct.id != null ? String(ct.id) : ''
          const courses = cid ? (this.courseTypeCoursesMap[cid] || []) : []
          return Array.isArray(courses) && courses.some(c => String(c?.courseName || c?.course_name || c?.name || '').trim().toLowerCase() === cur)
        })
        if (hit) return hit
      }
      return list[0]
    },
    currentCourseList() {
      const ct = this.currentCourseType
      const cid = ct && ct.id != null ? String(ct.id) : ''
      const list = cid ? (this.courseTypeCoursesMap[cid] || []) : []
      let arr = Array.isArray(list) ? list : []

      // 如果开启了只显示管理员课程的筛选，则过滤
      if (this.showAdminCoursesOnly) {
        arr = arr.filter(c => {
          const teacherName = String(c?.teacher_name || c?.teacherName || '').trim()
          return teacherName === '管理员'
        })
      }

      // repoId=0 表示未开放，不显示
      arr = arr.filter(c => {
        const rid = Number(c?.repoId ?? c?.repo_id)
        return !Number.isNaN(rid) && rid > 0
      })

      const toName = (c) => String(c?.courseName || c?.course_name || c?.name || '').trim()
      const isAmcLevel = (s) => /^amc\s*\d+/i.test(String(s || '').trim())
      const getAmcNum = (s) => {
        const m = String(s || '').match(/(\d+)/)
        return m ? Number(m[1]) : NaN
      }
      const amcItems = arr.filter(i => isAmcLevel(toName(i)))
      if (!amcItems.length) return arr
      return [...arr].sort((a, b) => {
        const an = toName(a)
        const bn = toName(b)
        const aIs = isAmcLevel(an)
        const bIs = isAmcLevel(bn)
        if (aIs && bIs) {
          return (getAmcNum(an) || 0) - (getAmcNum(bn) || 0)
        }
        if (aIs && !bIs) return -1
        if (!aIs && bIs) return 1
        return 0
      })
    },
    courseCenterTitle() {
      const ct = this.currentCourseType
      const code = String(ct?.code || ct?.name || '课程').trim()
      const head = code ? code.toUpperCase() : 'AMC'
      return `${head}备考中心`
    },
    currentCourseName() {
      const cur = String(this.selectedAmcLevel || '').trim()
      if (!cur) {
        const first = this.currentCourseList && this.currentCourseList[0]
        return String(first?.courseName || first?.course_name || first?.name || '').trim() || '请选择'
      }
      return cur.toUpperCase()
    },
    currentSelectedCourse() {
      const list = Array.isArray(this.currentCourseList) ? this.currentCourseList : []
      if (!list.length) return null
      const cur = String(this.selectedAmcLevel || this.selectedLevelDisplay || '').trim().toLowerCase()
      if (!cur) return list[0] || null
      return list.find(c => {
        const name = String(c?.courseName || c?.course_name || c?.name || '').trim().toLowerCase()
        return name === cur
      }) || list[0] || null
    },
    showCodePracticeEntry() {
      const featureCode = this.resolveFeatureCode('AMC_CODE_PRACTICE', this.getEffectiveCourseTypeCode())
      return !!this.getCodePracticeRepoId() && isFeatureActive(featureCode)
    },
    avatarUrl() {
      const { getUserAvatarUrl } = require('@/utils/minio')
      const raw = (this.$store && this.$store.state && this.$store.state.user && this.$store.state.user.avatar) || ''
      const processedUrl = getUserAvatarUrl(raw)
      return processedUrl || this.defaultAvatar
    },
    // 备考进度百分比（基于用户计划的总题数）
    studyProgress() {
      const total = this.planTotalQuestions || this.totalQuestions
      if (!total || total === 0) return 0
      const progress = Math.round((this.completed / total) * 100)
      return Math.min(100, Math.max(0, progress))
    },
    // 根据进度返回对应颜色：红(<25%)、橙(25-50%)、黄(50-75%)、绿(>=75%)
    progressColor() {
      const p = this.studyProgress
      if (p < 25) return '#ff4d4f' // 红色
      if (p < 50) return '#ff9800' // 橙色
      if (p < 75) return '#fadb14' // 黄色
      return '#52c41a' // 绿色
    }
  },
  data() {
    return {
      // 会员状态
      isMember: false,
      showPurchaseDialog: false,
      // 用户选择信息
      userLevel: null,
      userYear: null,
      isFirstTime: false,
      selectedAmcLevel: 'amc8',

      selectedLevelDisplay: '',

      selectedCourseTypeId: null,
      selectedCourseTypeCode: null,

      // 动态课程类型（t_course_type / teach-course-categories）与课程名（t_teachcourse.course_name）
      courseTypeList: [],
      courseTypeCourseNameMap: {},
      courseTypeCoursesMap: {},

      // 是否只显示管理员课程
      showAdminCoursesOnly: true,

      // t_repo 列表缓存（用于从 course_id + title 匹配 repoId）
      repoListCache: null,

      // AMC信息
      amcRepo: null,
      amcData: null,

      // 倒计时相关
      daysLeft: 0,
      examDate: '',

      // 统计数据
      accuracy: 0,
      completed: 0,
      totalQuestions: 0,
      planTotalQuestions: 0, // 用户计划的总题数
      unreviewedWrongCount: 0, // 未复习的错题数

      // 其他数据
      dailyStreak: 0,
      maxConsecutive: 0,

      // 计划天数
      dayIndex: 1,
      dayTotal: 18,

      // 今日任务统计
      todayStats: { done: 0, wrong: 0, review: 0 },
      dailyTarget: 3,

      // 本月完成天数
      finishedDaysThisMonth: 0,

      // 本月已完成的日期列表（yyyy-MM-dd 字符串）
      completedDatesThisMonth: [],

      // 本月日历基准日期（用于天数统计悬浮日历）
      currentMonthDate: new Date(),

      // 题库题目（页面加载时获取）
      repoQuestions: [],

      // 快速练习选择
      defaultAvatar: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNFNUU3RUIiLz4KPHN2ZyB4PSI4IiB5PSI4IiB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDEyQzE0LjIwOTEgMTIgMTYgMTAuMjA5MSAxNiA4QzE2IDUuNzkwODYgMTQuMjA5MSA0IDEyIDRDOS43OTA4NiA0IDggNS43OTA4NiA4IDhDOCAxMC4yMDkxIDkuNzkwODYgMTIgMTIgMTJaIiBmaWxsPSIjOUM5QzlDIi8+CjxwYXRoIGQ9Ik0xMiAxNEM5LjMzIDE0IDcuMDEgMTUuMjQgNS4zNiAxNy4yNEM2LjY3IDE5LjI0IDkuMTkgMjAgMTIgMjBDMTQuODEgMjAgMTcuMzMgMTkuMjQgMTguNjQgMTcuMjRDMTYuOTkgMTUuMjQgMTQuNjcgMTQgMTIgMTRaIiBmaWxsPSIjOUM5QzlDIi8+Cjwvc3ZnPgo8L3N2Zz4K',

      // 复习提醒开关
      reviewReminderEnabled: true,

      // 备考进度显示切换
      showProgressPercent: true, // true=显示百分比, false=显示已完成题数
      showExtendedFeatures: false,

      // 正确率轮播相关
      accuracyPage: 0, // 当前页码：0=总正确率, 1=近几日, 2=近几题
      currentAccuracyType: '', // 'days' 或 'questions'
      currentAccuracyValue: null, // 当前选中的天数或题数
      // 按天数统计的正确率
      accuracyByDays: [
        { days: 0, label: '今日', accuracy: 0 },
        { days: 3, label: '3日', accuracy: 0 },
        { days: 7, label: '7日', accuracy: 0 },
        { days: 10, label: '10日', accuracy: 0 }
      ],
      // 按题数统计的正确率
      accuracyByQuestions: [
        { count: 5, label: '5题', accuracy: 0 },
        { count: 10, label: '10题', accuracy: 0 },
        { count: 20, label: '20题', accuracy: 0 },
        { count: 50, label: '50题', accuracy: 0 }
      ],
      analyticsExporting: false,
      analyticsExportDots: '',
      analyticsExportTimer: null,
      analyticsExportFileName: ''
    }
  },
  created() {
    // 读取本地存储的提醒设置
    const savedReminder = localStorage.getItem('amc_review_reminder')
    this.reviewReminderEnabled = savedReminder === null ? true : (savedReminder === 'true')

    this.selectedLevelDisplay = String(this.$route.query.level || '').trim()
    this.selectedAmcLevel = this.$route.query.level || localStorage.getItem('selectedAmcLevel') || 'amc8'

    this.selectedCourseTypeId = this.$route.query.courseTypeId || null
    this.selectedCourseTypeCode = this.$route.query.courseTypeCode || null

    this.hideSidebarAndNavbar()
    // 直接检查用户选择，购买检查改为点击课程时触发
    Promise.resolve(this.loadCourseTypes())
      .catch(() => {})
      .finally(() => {
        this.checkUserSelection()
      })
    try { this.$store && this.$store.dispatch && this.$store.dispatch('user/getInfo') } catch (e) {}
  },
  activated() {
    // 当页面通过 keep-alive 重新激活时，刷新今日任务统计，确保“已复习”数量同步
    if (this.getAmcRepo()) {
      this.checkMemberStatus(true)
      this.refreshFeaturePermissions()
      this.loadTodayStats().then(() => {
        this.checkReviewStatus()
      })
      this.loadFinishedDaysThisMonth()
    }
  },
  mounted() {
    // 添加点击外部关闭选择器的监听
    document.addEventListener('click', this.handleClickOutside)
    // 监听今日任务统计刷新事件
    try {
      const { EventBus } = require('@/utils/eventBus')
      EventBus.$on('refresh-today-stats', this.loadTodayStats)
    } catch (_) {}
  },
  beforeDestroy() {
    // this.showSidebarAndNavbar()
    // 移除点击外部监听
    document.removeEventListener('click', this.handleClickOutside)
    this.stopAnalyticsExportAnimation()
    // 移除今日任务统计刷新事件监听
    try {
      const { EventBus } = require('@/utils/eventBus')
      EventBus.$off('refresh-today-stats', this.loadTodayStats)
    } catch (_) {}
  },
  methods: {
    normalizeCourseTypeCode(raw) {
      const s = String(raw || '').trim()
      if (!s) return ''
      const lower = s.toLowerCase()
      if (lower.includes('amc')) return 'AMC'
      if (lower.includes('kangaroo') || s.includes('袋鼠')) return 'KANGAROO'
      if (lower.includes('gesp') || lower.includes('ccf')) return 'GESP'
      return s.toUpperCase()
    },
    getEffectiveCourseTypeCode() {
      const ct = this.currentCourseType
      const raw = this.selectedCourseTypeCode || ct?.code || ct?.name || ''
      return this.normalizeCourseTypeCode(raw)
    },
    getCurrentSubjectCode() {
      if (this.isAmcMode()) {
        return this.resolveCourseLevel(this.selectedAmcLevel || this.selectedLevelDisplay || '').trim().toUpperCase()
      }
      return String(this.selectedLevelDisplay || '').trim().toUpperCase()
    },
    isAmcMode() {
      const code = this.getEffectiveCourseTypeCode().toLowerCase()
      if (code) return code.includes('amc')
      const lv = String(this.selectedAmcLevel || '').toLowerCase()
      return lv === 'amc8' || lv === 'amc10' || lv === 'amc12'
    },
    async getRepoListCached() {
      if (Array.isArray(this.repoListCache) && this.repoListCache.length) return this.repoListCache
      const res = await fetchRepoList({ pageNum: 1, pageSize: 1000 }).catch(() => null)
      const list = Array.isArray(res?.data)
        ? res.data
        : (Array.isArray(res?.data?.data) ? res.data.data : (Array.isArray(res) ? res : []))
      this.repoListCache = Array.isArray(list) ? list : []
      return this.repoListCache
    },
    async resolveRepoIdFromRepoTable() {
      // 优先使用路由中传入的 repoId
      const routeRepoId = Number(this.$route?.query?.repoId)
      if (routeRepoId && !Number.isNaN(routeRepoId)) {
        this.amcRepo = routeRepoId
        return routeRepoId
      }

      const titleRaw = (this.selectedLevelDisplay || this.selectedAmcLevel || '').toString().trim()
      const titleKey = titleRaw.toLowerCase()
      const courseId = this.selectedCourseTypeId != null ? Number(this.selectedCourseTypeId) : NaN
      if (!titleKey) return null

      const list = await this.getRepoListCached()
      const rows = Array.isArray(list) ? list : []
      const isDel = (r) => {
        const del = Number(r?.isDeleted != null ? r.isDeleted : r?.is_deleted)
        return del === 1
      }

      const matchTitle = (t) => {
        const s = String(t || '').trim().toLowerCase()
        if (!s) return false
        if (s === titleKey) return true
        return s.endsWith(titleKey) || s.includes(titleKey) || titleKey.includes(s)
      }

      const matchCourseId = (r) => {
        if (!courseId || Number.isNaN(courseId)) return true
        const cid = Number(r?.courseId != null ? r.courseId : r?.course_id)
        return cid === courseId
      }

      // 1) 优先：courseId + title 精确/包含匹配（最严格）
      let hit = rows.find(r => {
        if (!r) return false
        if (isDel(r)) return false
        if (!matchCourseId(r)) return false
        return matchTitle(r.title)
      })

      // 2) 兜底：如果未传 courseId 或 courseId 匹配不到，允许只按 title 全局匹配
      if (!hit) {
        hit = rows.find(r => {
          if (!r) return false
          if (isDel(r)) return false
          return matchTitle(r.title)
        })
      }

      const id = hit && hit.id != null ? Number(hit.id) : null
      if (id && !Number.isNaN(id)) {
        this.amcRepo = id
        return id
      }
      return null
    },
    async loadCourseTypes() {
      try {
        const res = await getCourseCategories()
        const list = (res && res.code === 1 && Array.isArray(res.data)) ? res.data : []
        this.courseTypeList = list

        const map = {}
        const coursesMap = {}
        await Promise.all(list.map(async (ct) => {
          try {
            const categoryId = ct && ct.id
            if (!categoryId) return
            const courses = await getCoursesByCategoryId(categoryId)
            const arr = Array.isArray(courses) ? courses : []
            coursesMap[String(categoryId)] = arr
            const first = arr[0] || null
            const name = (first && (first.courseName || first.course_name || first.name)) || ''
            map[String(categoryId)] = name
          } catch (e) {
            // ignore
          }
        }))
        this.courseTypeCourseNameMap = map
        this.courseTypeCoursesMap = coursesMap

        // 如果本地/路由 level 能在 courseTypeList 里匹配到，则用它；否则保持原有默认
        const cur = String(this.selectedAmcLevel || '').toLowerCase()
        if (cur && list.length) {
          const hit = list.find(i => String(i.code || i.name || '').toLowerCase() === cur)
          if (hit) {
            this.selectedAmcLevel = String(hit.code || hit.name || '').toLowerCase()
          }
        }
        await this.refreshFeaturePermissions()
        this.ensureSelectedCourseOpen()
      } catch (e) {
        this.courseTypeList = []
        this.courseTypeCourseNameMap = {}
        this.courseTypeCoursesMap = {}
      }
    },
    ensureSelectedCourseOpen() {
      const list = Array.isArray(this.currentCourseList) ? this.currentCourseList : []
      if (!list.length) return
      const cur = String(this.selectedAmcLevel || '').trim().toLowerCase()
      const toName = (c) => String(c?.courseName || c?.course_name || c?.name || '').trim()
      const exists = cur && list.some(c => toName(c).toLowerCase() === cur)
      if (exists) return

      const firstName = toName(list[0])
      if (!firstName) return
      this.selectedAmcLevel = firstName.toLowerCase()
      this.selectedLevelDisplay = firstName
      try { localStorage.setItem('selectedAmcLevel', this.selectedAmcLevel) } catch (_) {}
      try {
        this.$router && this.$router.replace && this.$router.replace({
          query: { ...(this.$route?.query || {}), level: firstName }
        }).catch(() => {})
      } catch (_) {}
    },
    async refreshFeaturePermissions() {
      try {
        await ensureFeatureStatusMap()
        await ensureEnabledFeatures(this.getEffectiveCourseTypeCode(), this.getCurrentSubjectCode())
      } catch (e) {
        console.error('加载功能权限失败:', e)
      }
    },
    resolveFeatureCode(featureCode, courseTypeCode) {
      const raw = String(featureCode || '').trim().toUpperCase()
      if (!raw) return raw
      const ct = String(courseTypeCode || this.getEffectiveCourseTypeCode() || '').trim().toUpperCase()
      if (!ct || ct === 'AMC') return raw
      if (raw.startsWith('AMC_')) {
        return `${ct}_${raw.substring(4)}`
      }
      return raw
    },
    async ensureFeatureAccess(featureCode) {
      try {
        const ct = this.getEffectiveCourseTypeCode()
        const sc = this.getCurrentSubjectCode()
        const mappedFeatureCode = this.resolveFeatureCode(featureCode, ct)
        await ensureFeatureStatusMap()
        if (!isFeatureActive(mappedFeatureCode)) {
          this.$message.info('敬请期待')
          return false
        }
        await ensureEnabledFeatures(ct, sc)
        if (hasFeature(mappedFeatureCode, ct, sc)) {
          return true
        }
        const ok = await this.checkMemberStatus(true, sc)
        if (ok) {
          await ensureEnabledFeatures(ct, sc)
        }
        if (hasFeature(mappedFeatureCode, ct, sc)) {
          return true
        }
        this.showPurchaseDialog = true
        return false
      } catch (e) {
        console.error('功能权限校验失败:', e)
        this.showPurchaseDialog = true
        return false
      }
    },
    getCourseNameByType(courseType) {
      const id = courseType && courseType.id != null ? String(courseType.id) : ''
      const fromMap = id ? (this.courseTypeCourseNameMap[id] || '') : ''
      if (fromMap) return fromMap
      return String(courseType?.code || courseType?.name || '').toUpperCase()
    },
    resolveCourseLevel(level) {
      const p = (level || '').toString().trim()
      if (p) return p

      const v = (this.selectedAmcLevel || localStorage.getItem('selectedAmcLevel') || '').toString().trim().toLowerCase()
      if (v === 'amc10') return 'AMC10'
      if (v === 'amc12') return 'AMC12'
      return 'AMC8'
    },
    goToSubjectSelect() {
      this.$router.push('/subject-select')
    },

    async handleLevelChange(level) {
      const raw = String(level || this.selectedAmcLevel || '').trim()
      this.selectedLevelDisplay = raw
      const nextLevel = raw.toLowerCase()

      // 尝试从 currentCourseList 中查找对应课程对象，获取其 repoId
      let resolvedRepoId = null
      if (Array.isArray(this.currentCourseList)) {
        const found = this.currentCourseList.find(c => {
          const cName = String(c?.courseName || c?.course_name || c?.name || '').trim().toLowerCase()
          return cName === nextLevel
        })
        if (found) {
          const rid = Number(found.repoId || found.repo_id)
          if (rid > 0) resolvedRepoId = rid
        }
      }

      const levelToRepo = { 'amc8': 14, 'amc10': 15, 'amc12': 16 }
      const targetRepo = resolvedRepoId || levelToRepo[nextLevel]

      // 非 AMC 的 LEVEL（例如袋鼠数学），目前页面后续逻辑依赖 repoId/学习计划：这里先仅更新 UI，避免报错
      if (!targetRepo) {
        this.userLevel = nextLevel
        this.selectedAmcLevel = nextLevel
        this.amcRepo = null // 清除当前缓存的 repoId
        try {
          localStorage.setItem('selectedAmcLevel', nextLevel)
        } catch (e) {}
        try {
          const q = { ...(this.$route.query || {}), level: nextLevel }
          // 切换等级时，务必移除旧的 repoId，否则 resolveRepoIdFromRepoTable 会优先取 query.repoId 导致一直取到旧数据
          if (q.repoId) delete q.repoId
          this.$router.replace({ path: this.$route.path, query: q })
        } catch (e) {}
        // 非 AMC：与 AMC 一样必须有学习计划，统一走 loadUserData 的计划校验逻辑
        try {
          await this.loadUserData()
        } catch (e) {}
        return
      }

      // AMC 等级切换：尝试同步 courseTypeId（如果能找到匹配大类）
      try {
        const ct = this.currentCourseType
        if (ct && ct.id != null) {
          this.selectedCourseTypeId = String(ct.id)
        }
      } catch (e) {}

      try {
        const userInfo = getTokenInfo()
        const userId = userInfo ? userInfo.id : null
        if (!userId) {
          this.$message.error('请先登录')
          return
        }

        // 使用通用接口查询
        const planRes = await getPlanByUserIdAndRepoId(userId, targetRepo, this.$route?.query?.sessionCode)
        const matchedPlan = planRes?.data

        if (!matchedPlan) {
          this.$message.info(`您还没有 ${nextLevel.toUpperCase()} 的学习计划，请先创建`)
          this.$router.replace({ path: '/amc-level-select', query: { level: nextLevel } })
          return
        }

        // 有该级别的计划，更新状态并加载数据
        this.amcRepo = targetRepo
        this.amcData = null
        this.userLevel = nextLevel
        this.selectedAmcLevel = nextLevel
        try {
          localStorage.setItem('selectedAmcLevel', nextLevel)
          localStorage.setItem('current_repo_id', String(targetRepo))
        } catch (e) {}

        // 同步 URL query
        try {
          const q = { ...(this.$route.query || {}), level: nextLevel }
          // 如果已经确认了 targetRepo（即新的 repoId），则将其写入 query
          // 这样 loadUserData -> resolveRepoIdFromRepoTable 就能直接取到正确的 repoId
          if (targetRepo) {
            q.repoId = String(targetRepo)
          }
          this.$router.replace({ path: this.$route.path, query: q })
        } catch (e) {}

        // 如果该计划不是当前激活的，需要先激活它
        if (matchedPlan.isActive !== 1) {
          try {
            await setActivePlan(userId, matchedPlan.id)
          } catch (e) {
            console.error('激活计划失败:', e)
            try {
              await updatePlan({ id: matchedPlan.id, isActive: 1 })
            } catch (_) {}
          }
        }

        // 切换等级后重新加载数据
        this.loadUserData()

        // 会员粒度按等级：切换等级后静默刷新会员状态（不弹窗）
        try {
          const lvl = this.resolveCourseLevel(nextLevel)
          await this.checkMemberStatus(true, lvl)
          await this.refreshFeaturePermissions()
        } catch (e) {}
      } catch (error) {
        console.error('检查计划失败:', error)
        this.$message.error('检查计划失败，请重试')
      }
    },
    // 根据API返回的examDate字段计算考试日期和剩余天数
    // examDate 形如："2027-01-22 00:00:00"
    calculateExamDateAndDaysLeftByExamDate(examDate) {
      try {
        const s = String(examDate || '').trim()
        if (!s) {
          this.updateDataBasedOnLevel()
          return
        }

        // 兼容 "yyyy-MM-dd HH:mm:ss" / "yyyy-MM-ddTHH:mm:ss" 两种
        const normalized = s.includes('T') ? s : s.replace(' ', 'T')
        const d = new Date(normalized)
        if (isNaN(d.getTime())) {
          this.updateDataBasedOnLevel()
          return
        }

        const today = new Date()
        const timeDiff = d.getTime() - today.getTime()
        const daysLeft = Math.ceil(timeDiff / (1000 * 3600 * 24))

        const yy = d.getFullYear()
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        this.examDate = `${yy}年${mm}月${dd}日`
        this.daysLeft = Math.max(0, daysLeft)

        console.log(`考试日期: ${this.examDate}, 剩余天数: ${this.daysLeft}`)
      } catch (error) {
        console.error('计算考试日期失败:', error)
        this.updateDataBasedOnLevel()
      }
    },
    // 检查会员状态（点击课程时调用）
    async checkMemberStatus(silent = false, courseLevel) {
      try {
        const tokenInfo = getTokenInfo()
        if (!tokenInfo || !tokenInfo.id) {
          if (!silent) {
            this.$message.error('请先登录')
          }
          return false
        }

        let courseType = 'AMC'
        let lvl = ''
        if (this.isAmcMode()) {
          lvl = this.resolveCourseLevel(courseLevel)
          courseType = 'AMC'
        } else {
          const ctCode = this.getEffectiveCourseTypeCode()
          courseType = (ctCode || 'COURSE').toUpperCase()
          lvl = String(courseLevel || this.selectedLevelDisplay || '').trim()
        }
        const res = await getMyUserMembership({ courseTypeCode: courseType, subjectCode: lvl || undefined })
        const membership = (res && res.code === 1) ? res.data : null
        if (membership && String(membership.tierCode || '').toUpperCase() === 'VIP') {
          this.isMember = true
          return true
        } else {
          // 未开通会员
          this.isMember = false
          if (!silent) {
            this.showPurchaseDialog = true
          }
          return false
        }
      } catch (e) {
        console.error('检查会员状态失败:', e)
        // 出错时也显示开通弹窗
        if (!silent) {
          this.showPurchaseDialog = true
        }
        return false
      }
    },

    // 打开开通会员弹窗
    openMemberDialog() {
      if (this.isMember) {
        this.$message.info(`您已是${this.memberDisplayName}会员，无需重复开通`)
        return
      }
      this.showPurchaseDialog = true
    },

    // 开通会员成功回调
    async handlePurchased() {
      this.isMember = true
      this.showPurchaseDialog = false
      // 清除功能权限缓存并重新加载，使 VIP 功能立即可用
      try {
        const { clearCachedEnabledFeatures } = require('@/utils/feature')
        clearCachedEnabledFeatures()
      } catch (_) {}
      await this.refreshFeaturePermissions()
    },

    // 弹窗关闭回调
    handlePurchaseClose() {
      // 关闭弹窗，不做任何操作
    },

    // 隐藏侧边栏和导航栏
    hideSidebarAndNavbar() {
      this.$store.dispatch('app/closeSideBar', { withoutAnimation: false })

      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')

      if (sidebar) {
        sidebar.style.display = 'none'
      }
      if (navbar) {
        navbar.style.display = 'none'
      }

      const appMain = document.querySelector('.app-main')
      if (appMain) {
        appMain.style.padding = '0'
        appMain.style.margin = '0'
      }
    },

    // 获取"第几天/共几天"（使用通用接口）
    async loadDayProgressFromAmcInfo() {
      try {
        const token = getTokenInfo && getTokenInfo()
        const userId = token ? (token.id ?? token.userId) : null
        if (!userId) return

        const repoId = this.getAmcRepo()
        if (!repoId) return

        const planRes = await getPlanByUserIdAndRepoId(userId, repoId, this.$route?.query?.sessionCode)
        const plan = planRes?.data
        if (!plan) return

        // 计算总天数
        const totalMinutes = Number(plan.availableStudyMinutesSnapshot || 0)
        const minutesPerDay = Number(plan.dailyStudyMinutes || 0)
        if (totalMinutes > 0 && minutesPerDay > 0) {
          this.dayTotal = Math.max(1, Math.ceil(totalMinutes / minutesPerDay))
        }
      } catch (e) {
        console.warn('加载计划天数失败:', e)
      }
    },

    // 从通用进度表获取每日应做数量
    async loadDailyTargetFromProgress() {
      try {
        const repoId = this.getAmcRepo()
        const token = getTokenInfo && getTokenInfo()
        const userId = token ? (token.id ?? token.userId) : null
        if (!userId || !repoId) return

        const today = this.getTodayStr()
        // 使用通用接口查询当日进度
        const progressRes = await getProgressByDate(userId, repoId, today)
        const progress = progressRes?.data
        if (progress) {
          const targetVal = progress.plannedCount ?? progress.planned_count
          if (targetVal != null) {
            this.dailyTarget = Number(targetVal) || this.dailyTarget
          }
        }
      } catch (e) {
        console.warn('加载每日应做数量失败:', e)
      }
    },

    // 获取 YYYY-MM-DD 字符串
    getTodayStr() {
      const d = new Date()
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },

    // 统计本月完成的天数（使用通用接口）
    async loadFinishedDaysThisMonth(baseDate) {
      try {
        const repoId = this.getAmcRepo()
        const token = getTokenInfo && getTokenInfo()
        const userId = token ? (token.id ?? token.userId) : null
        if (!userId || !repoId) return

        const d = baseDate instanceof Date ? baseDate : this.currentMonthDate
        const year = d.getFullYear()
        const monthIndex = d.getMonth() // 0-11
        const month = monthIndex + 1
        const startStr = `${year}-${String(month).padStart(2, '0')}-01`
        const endDate = new Date(year, month, 0)
        const endStr = `${year}-${String(month).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`
        // 修正：countCompletedDays 参数为 (userId, repoId, dateFrom, dateTo)
        const res = await countCompletedDays(userId, repoId, startStr, endStr)
        const payload = res?.data?.data ?? res?.data ?? {}

        let days = 0
        let dates = []

        if (typeof payload === 'number') {
          days = Number(payload) || 0
        } else {
          days = Number(payload.days ?? 0) || 0
          dates = Array.isArray(payload.dates) ? payload.dates.slice() : []
        }

        this.finishedDaysThisMonth = days
        this.completedDatesThisMonth = dates
      } catch (e) {
        console.warn('统计本月完成天数失败:', e)
        this.finishedDaysThisMonth = 0
        this.completedDatesThisMonth = []
      }
    },

    handleCalendarInput(val) {
      if (!(val instanceof Date)) return
      this.currentMonthDate = val
      this.loadFinishedDaysThisMonth(val)
    },

    // 加载今日任务统计（使用通用接口）
    async loadTodayStats() {
      try {
        const repoId = this.getAmcRepo()
        const token = getTokenInfo && getTokenInfo()
        const userId = token ? (token.id ?? token.userId) : null
        if (!userId || !repoId) return

        const todayStr = this.getTodayStr()
        console.log('[今日任务] 查询日期:', todayStr, '当前时间:', new Date().toLocaleString())

        // 使用通用接口查询当日进度
        const progressRes = await getProgressByDate(userId, repoId, todayStr)
        const progress = progressRes?.data

        console.log('[今日任务] 返回数据:', progress)

        if (progress) {
          const planned = Number(progress.plannedCount ?? progress.planned_count ?? 0)
          const finished = Number(progress.finishedCount ?? progress.finished_count ?? 0)
          const correct = Number(progress.correctCount ?? progress.correct_count ?? 0)
          let wrong = progress.wrongCount ?? progress.wrong_count
          if (wrong == null) wrong = Math.max(0, finished - correct)
          const review = Number(progress.reviewDone ?? progress.review_done ?? 0)

          console.log('[今日任务] 解析结果 - 计划:', planned, '完成:', finished, '错误:', wrong, '复习:', review)

          this.dailyTarget = planned || this.dailyTarget
          this.todayStats.done = finished || 0
          this.todayStats.wrong = Number(wrong) || 0
          this.todayStats.review = review || 0
        } else {
          console.log('[今日任务] 今天没有任务记录，显示空数据')
          this.todayStats = { done: 0, wrong: 0, review: 0 }
        }
      } catch (e) {
        console.warn('加载今日任务统计失败:', e)
      }
    },
    goMyself() {
      this.$router.push({
        path: '/amc-profile',
        query: {
          level: this.selectedLevelDisplay || this.selectedAmcLevel,
          courseTypeId: this.selectedCourseTypeId,
          courseTypeCode: this.selectedCourseTypeCode
        }
      })
    },
    async logout() {
      this.$store.dispatch && this.$store.dispatch('logoutUser')
      await (this.$store.dispatch && this.$store.dispatch('user/logout'))
      this.$router.push('/login')
    },
    handleAvatarError(event) {
      event.target.src = this.defaultAvatar
    },

    // 查询连对挑战最高记录（按当前用户）
    async loadMaxChallengeCount() {
      try {
        const userInfo = getTokenInfo && getTokenInfo()
        const userId = userInfo ? userInfo.id : null
        console.log('[AMCCourse] 请求连对挑战最大记录: /amc-status/maxChallengeCount', userId ? `(userId=${userId})` : '')
        const resp = await getMaxChallengeCount(userId)
        console.log('[AMCCourse] 连对挑战最大记录响应 =', resp)
        const val = typeof resp?.data === 'number' ? resp.data : 0
        this.maxConsecutive = val || 0
      } catch (e) {
        console.warn('获取连对挑战最大记录失败:', e)
      }
    },

    // 加载用户做题统计（按 repo 计数）- 使用通用接口
    async loadUserQuestionStats() {
      try {
        const repoId = this.getAmcRepo()
        console.log('[AMCCourse] loadUserQuestionStats repoId =', repoId)
        if (!repoId) {
          console.warn('[AMCCourse] 缺少 repoId，跳过已做数量统计')
          return
        }
        const userInfo = getTokenInfo && getTokenInfo()
        const userId = userInfo ? (userInfo.id ?? userInfo.userId) : null
        if (!userId) {
          console.warn('[AMCCourse] 缺少 userId，跳过用户维度的统计')
          return
        }
        // 使用通用接口：/user-question-records/countByRepoId/{repoId}
        console.log('[AMCCourse] 请求已做数量: /user-question-records/countByRepoId/', repoId, `?userId=${userId}`)
        const res = await countRecordsByRepoId(repoId, userId)
        console.log('[AMCCourse] 已做数量响应 =', res)
        const done = typeof res?.data === 'number' ? res.data : 0
        this.completed = done

        // 使用通用接口获取正确率：/user-question-records/accuracyByRepoId/{repoId}
        console.log('[AMCCourse] 请求正确率: /user-question-records/accuracyByRepoId/', repoId, `?userId=${userId}`)
        const accRes = await getAccuracyByRepoId(repoId, userId)
        console.log('[AMCCourse] 正确率响应 =', accRes)
        const ratio = typeof accRes?.data === 'number' ? accRes.data : 0
        this.accuracy = Math.round(Math.max(0, Math.min(1, ratio)) * 100)
        // 初始化当前显示的正确率为总正确率
        this.currentAccuracy = this.accuracy
      } catch (e) {
        console.warn('按题库统计用户已做数量失败:', e)
      }
    },

    // 加载不同维度的正确率数据
    async loadAccuracyStats() {
      try {
        const repoId = this.getAmcRepo()
        if (!repoId) return
        const userInfo = getTokenInfo && getTokenInfo()
        const userId = userInfo ? (userInfo.id ?? userInfo.userId) : null
        if (!userId) return

        // 并行加载按天数和按题数的正确率
        const dayPromises = this.accuracyByDays.map(async (item) => {
          try {
            const res = await accuracyByDays(repoId, userId, item.days)
            const ratio = typeof res?.data === 'number' ? res.data : 0
            item.accuracy = Math.round(Math.max(0, Math.min(1, ratio)) * 100)
          } catch (e) {
            console.warn(`加载${item.label}正确率失败:`, e)
            item.accuracy = 0
          }
        })

        const quesPromises = this.accuracyByQuestions.map(async (item) => {
          try {
            const res = await accuracyByLastN(repoId, userId, item.count)
            const ratio = typeof res?.data === 'number' ? res.data : 0
            item.accuracy = Math.round(Math.max(0, Math.min(1, ratio)) * 100)
          } catch (e) {
            console.warn(`加载近${item.label}正确率失败:`, e)
            item.accuracy = 0
          }
        })

        await Promise.all([...dayPromises, ...quesPromises])
      } catch (e) {
        console.warn('加载正确率统计失败:', e)
      }
    },

    // 加载未复习错题数量
    async loadUnreviewedWrongCount() {
      try {
        const repoId = this.getAmcRepo()
        if (!repoId) return
        const userInfo = getTokenInfo && getTokenInfo()
        const userId = userInfo ? (userInfo.id ?? userInfo.userId) : null
        if (!userId) return

        const res = await unreviewedWrongCount(repoId, userId)
        this.unreviewedWrongCount = Number(res?.data) || 0
      } catch (e) {
        console.warn('加载未复习错题数量失败:', e)
        this.unreviewedWrongCount = 0
      }
    },

    // 加载用户计划的总题数（使用通用接口）
    async loadPlanTotalQuestions() {
      try {
        const repoId = this.getAmcRepo()
        if (!repoId) return
        const userInfo = getTokenInfo && getTokenInfo()
        const userId = userInfo ? (userInfo.id ?? userInfo.userId) : null
        if (!userId) return

        const schemeRes = await getSchemeByUserIdAndRepoId(userId, repoId)
        const scheme = schemeRes?.data

        if (scheme && scheme.questionId) {
          const questionIds = scheme.questionId.split(',').filter(id => id.trim())
          this.planTotalQuestions = questionIds.length
        } else {
          this.planTotalQuestions = 0
        }
      } catch (e) {
        console.warn('加载计划总题数失败:', e)
        this.planTotalQuestions = 0
      }
    },

    // 上一页
    prevAccuracyPage() {
      this.accuracyPage = this.accuracyPage > 0 ? this.accuracyPage - 1 : 2
    },

    // 下一页
    nextAccuracyPage() {
      this.accuracyPage = this.accuracyPage < 2 ? this.accuracyPage + 1 : 0
    },

    // 选择正确率卡片（高亮显示）
    selectAccuracyCard(type, value, label, accuracy) {
      if (this.currentAccuracyType === type && this.currentAccuracyValue === value) {
        // 再次点击取消选中
        this.currentAccuracyType = ''
        this.currentAccuracyValue = null
      } else {
        this.currentAccuracyType = type
        this.currentAccuracyValue = value
      }
    },

    // 点击页面其他地方（保留但不再需要关闭弹窗）
    handleClickOutside(event) {
      // 轮播模式不需要关闭弹窗
    },

    // 切换备考进度显示模式（百分比/已完成题数）
    toggleProgressDisplay() {
      this.showProgressPercent = !this.showProgressPercent
    },

    toggleExtendedFeatures() {
      this.showExtendedFeatures = !this.showExtendedFeatures
    },

    buildCourseCenterQuery(extra = {}) {
      const query = {
        ...(this.$route?.query || {})
      }
      const repoId = this.getAmcRepo()
      if (repoId) query.repoId = repoId
      if (this.selectedAmcLevel) query.level = this.selectedAmcLevel
      if (this.selectedLevelDisplay && !query.level) query.level = this.selectedLevelDisplay
      if (this.selectedCourseTypeId) query.courseTypeId = this.selectedCourseTypeId
      if (this.selectedCourseTypeCode) query.courseTypeCode = this.selectedCourseTypeCode
      return { ...query, ...extra }
    },
    getDefaultCodePracticeRepoId(courseTypeCode) {
      const ct = String(courseTypeCode || this.getEffectiveCourseTypeCode() || '').trim().toUpperCase()
      const map = {
        GESP: 27
      }
      return map[ct] || null
    },
    getCodePracticeRepoId() {
      const course = this.currentSelectedCourse || {}
      const raw = course.codeRepoId ?? course.code_repo_id ?? course.practiceRepoId ?? course.practice_repo_id ?? this.$route?.query?.codeRepoId ?? this.$route?.query?.code_repo_id ?? this.getDefaultCodePracticeRepoId()
      const id = Number(raw)
      return !Number.isNaN(id) && id > 0 ? id : null
    },

    goToWrongQuestions() {
      const repoId = this.getAmcRepo()
      this.$router.push({
        path: '/wrong-questions',
        query: this.buildCourseCenterQuery({ repoId, status: 'pending' })
      })
    },

    // 恢复侧边栏和导航栏
    showSidebarAndNavbar() {
      this.$store.dispatch('app/openSideBar', { withoutAnimation: false })

      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')

      if (sidebar) {
        sidebar.style.display = ''
      }
      if (navbar) {
        navbar.style.display = ''
      }

      const appMain = document.querySelector('.app-main')
      if (appMain) {
        appMain.style.padding = ''
        appMain.style.margin = ''
      }
    },

    // 检查用户选择状态
    checkUserSelection() {
      // 从路由参数获取用户选择
      const { level, year, knowledgeBackground } = this.$route.query

      if (level && year) {
        // 用户已经选择了级别和年份
        this.userLevel = level
        this.selectedAmcLevel = level
        this.userYear = year
        this.isFirstTime = false
        this.loadUserData()
      } else {
        // 检查localStorage中是否有用户选择
        const savedSelection = localStorage.getItem('amc_user_selection')
        if (savedSelection) {
          try {
            const selection = JSON.parse(savedSelection)
            this.userLevel = selection.level
            this.selectedAmcLevel = selection.level
            this.userYear = selection.year
            this.isFirstTime = false
            this.loadUserData()
          } catch (error) {
            console.error('解析用户选择失败:', error)
            // 无查询参数且本地无有效选择时，直接尝试按userId加载后端数据，避免循环跳转
            this.loadUserData()
          }
        } else {
          // 无查询参数且本地无选择，直接尝试按userId加载后端数据，避免循环跳转
          this.loadUserData()
        }
      }
    },

    // 跳转到级别选择页面
    redirectToLevelSelect() {
      const query = {}
      if (this.selectedCourseTypeId) query.courseTypeId = this.selectedCourseTypeId
      if (this.selectedCourseTypeCode) query.courseTypeCode = this.selectedCourseTypeCode
      if (this.amcRepo) query.repoId = this.amcRepo
      this.$router.replace({ path: '/amc-level-select', query })
    },

    // 加载用户数据（使用通用接口）
    async loadUserData() {
      try {
        const userInfo = getTokenInfo()
        const userId = userInfo ? userInfo.id : null
        const routeSessionCode = String(this.$route?.query?.sessionCode || '').trim().toUpperCase()
        const matchesSessionCode = (plan) => {
          const planSessionCode = String(plan?.sessionCode || '').trim().toUpperCase()
          if (!routeSessionCode) {
            return true
          }
          return planSessionCode === routeSessionCode
        }
        const pickPlanWithSessionFallback = (list) => {
          const plans = Array.isArray(list) ? list : []
          if (!plans.length) return null
          const exactPlans = plans.filter(matchesSessionCode)
          const targetList = exactPlans.length ? exactPlans : plans
          return targetList.find(p => Number(p?.isActive) === 1) || targetList[0] || null
        }

        if (!userId) {
          console.log('无法获取用户ID，跳转到级别选择页面')
          this.$message.info('请先选择AMC级别')
          this.$router.replace('/amc-level-select')
          return
        }

        // 页面加载时静默检查会员状态
        this.checkMemberStatus(true)

        // 非 AMC（如袋鼠）：与 AMC 一样要求必须存在学习计划。优先按 level 解析 repoId，失败时回退到已存在学习计划
        if (!this.isAmcMode()) {
          const planRes = await getPlansByUserId(userId)
          const plans = Array.isArray(planRes?.data) ? planRes.data : []

          let rid = await this.resolveRepoIdFromRepoTable()
          if (!rid) {
            const selectedCourseTypeIdNum = Number(this.selectedCourseTypeId)
            const byCourseType = plans.filter(p => {
              if (!selectedCourseTypeIdNum || Number.isNaN(selectedCourseTypeIdNum)) return true
              return Number(p?.courseType) === selectedCourseTypeIdNum
            })
            const fallbackPlan = byCourseType.find(p => Number(p?.isActive) === 1) || byCourseType[0] || null
            if (fallbackPlan && Number(fallbackPlan?.repoId) > 0) {
              rid = Number(fallbackPlan.repoId)
              this.amcRepo = rid
              try {
                const q = { ...(this.$route.query || {}) }
                if (!q.repoId) {
                  q.repoId = String(rid)
                  this.$router.replace({ path: this.$route.path, query: q })
                }
              } catch (e) {}
            }
          }

          if (!rid) {
            this.accuracy = 0
            this.currentAccuracy = 0
            this.unreviewedWrongCount = 0
            this.totalQuestions = 0
            this.completed = 0
            this.planTotalQuestions = 0
            this.daysLeft = 0
            this.examDate = ''
            this.$message.warning('未找到对应题库，请联系管理员配置题库')
            return
          }

          const planList = plans.filter(p => Number(p?.repoId) === Number(rid))
          const matchedPlan = pickPlanWithSessionFallback(planList)

          if (!matchedPlan) {
            this.$message.info('请先制定学习计划')
            const selectQuery = {
              level: this.selectedLevelDisplay || this.selectedAmcLevel,
              courseTypeId: this.selectedCourseTypeId,
              courseTypeCode: this.selectedCourseTypeCode
            }
            if (this.amcRepo) selectQuery.repoId = this.amcRepo
            this.$router.replace({
              path: '/amc-level-select',
              query: selectQuery
            })
            return
          }

          // 转换为兼容旧格式（复用 updateDataFromApi：倒计时取 examDate/year，统计取 repoId）
          const compatiblePlan = {
            ...matchedPlan,
            amcRepo: matchedPlan.repoId,
            amcMark: matchedPlan.isActive,
            level: matchedPlan.knowledgeLevel,
            year: matchedPlan.targetYear
          }
          this.updateDataFromApi([compatiblePlan])
          return
        }

        const selectedLevel = this.userLevel || localStorage.getItem('selectedAmcLevel') || 'amc8'
        const selectedYear = this.userYear

        // 使用通用接口查询
        const planRes = await getPlansByUserId(userId)
        const plans = planRes?.data || []

        // 从 t_repo 表动态获取 AMC 的 repoId
        const repoRes = await fetchRepoList({ pageNum: 1, pageSize: 1000 }).catch(() => null)
        const repoList = Array.isArray(repoRes?.data)
          ? repoRes.data
          : (Array.isArray(repoRes?.data?.data) ? repoRes.data.data : [])

        // 找出所有 AMC 相关的 repo 并构建映射
        const levelToRepo = {}
        const amcRepoIds = []
        repoList.forEach(r => {
          if (Number(r?.isDeleted || r?.is_deleted) === 1) return
          const title = String(r?.title || '').toUpperCase()
          if (title === 'AMC8' || title === 'AMC10' || title === 'AMC12') {
            const rid = Number(r?.id)
            amcRepoIds.push(rid)
            levelToRepo[title.toLowerCase()] = rid
          }
        })

        const targetRepo = levelToRepo[selectedLevel]

        // 根据 repoId 判断是否为 AMC 计划
        const amcPlans = (Array.isArray(plans) ? plans : []).filter(p => {
          const rid = Number(p?.repoId)
          return amcRepoIds.includes(rid)
        })

        const pickPlan = (arr) => {
          return pickPlanWithSessionFallback(arr)
        }

        let matchedPlan = null
        if (amcPlans.length > 0) {
          // 优先匹配：等级 + 激活 + 年份
          if (selectedYear) {
            matchedPlan = pickPlan(amcPlans.filter(p => Number(p.repoId) === targetRepo && String(p.targetYear) === String(selectedYear)))
          }
          // 降级匹配：等级 + 激活
          if (!matchedPlan) {
            matchedPlan = pickPlan(amcPlans.filter(p => Number(p.repoId) === targetRepo))
          }
          // 再降级：任意 AMC 计划
          if (!matchedPlan) {
            matchedPlan = pickPlan(amcPlans)
          }
        }

        if (!matchedPlan) {
          console.log('用户暂无学习计划，跳转到级别选择页面')
          this.$message.info('请先选择AMC级别')
          this.$router.replace({ path: '/amc-level-select', query: { level: selectedLevel } })
          return
        }

        // 转换为兼容旧格式
        const compatiblePlan = {
          ...matchedPlan,
          amcRepo: matchedPlan.repoId,
          amcMark: matchedPlan.isActive,
          level: matchedPlan.knowledgeLevel,
          year: matchedPlan.targetYear
        }
        this.updateDataFromApi([compatiblePlan])
        console.log('加载用户数据:', { level: this.userLevel, year: this.userYear })
      } catch (error) {
        console.error('获取用户学习计划失败:', error)
        const selectedLevel = this.userLevel || localStorage.getItem('selectedAmcLevel') || 'amc8'
        this.$message.info('请先选择AMC级别')
        this.$router.replace({ path: '/amc-level-select', query: { level: selectedLevel } })
      }
    },

    // 根据API数据更新界面
    updateDataFromApi(amcData) {
      // 根据API返回的数据更新界面显示
      // 这里可以根据实际的数据结构来解析和设置数据
      console.log('根据API数据更新界面:', amcData)

      // 保存AMC数据
      this.amcData = amcData

      // 示例：如果API返回了用户的学习进度数据
      if (amcData.length > 0) {
        const userData = amcData[0] // 假设取第一条记录

        // 保存amcRepo值
        if (userData.amcRepo !== undefined) {
          this.amcRepo = userData.amcRepo
        }

        // 根据用户数据更新统计信息
        if (userData.accuracy !== undefined) {
          this.accuracy = userData.accuracy
        }
        if (userData.completed !== undefined) {
          this.completed = userData.completed
        }
        if (userData.totalQuestions !== undefined) {
          this.totalQuestions = userData.totalCount
        }
        if (userData.dailyStreak !== undefined) {
          this.dailyStreak = userData.dailyStreak
        }
        if (userData.maxConsecutive !== undefined) {
          this.maxConsecutive = userData.maxConsecutive
        }

        // 优先使用后端返回的 examDate 来显示倒计时（year 可能为 null）
        if (userData.examDate) {
          this.calculateExamDateAndDaysLeftByExamDate(userData.examDate)
        } else if (userData.year) {
          // 兜底：根据API返回的year字段计算考试日期和剩余天数
          this.calculateExamDateAndDaysLeft(userData.year)
        }
      }

      // 如果API没有返回year字段，仍然根据级别设置考试时间
      if (!amcData.length || (!amcData[0].examDate && !amcData[0].year)) {
        this.updateDataBasedOnLevel()
      }

      // 一旦拿到 amcRepo，加载题库题目
      this.loadRepoQuestions()

      // 统计用户做题数与正确率
      this.loadUserQuestionStats()

      // 加载不同维度的正确率数据
      this.loadAccuracyStats()

      // 加载未复习错题数量
      this.loadUnreviewedWrongCount()

      // 加载用户计划的总题数
      this.loadPlanTotalQuestions()

      // 查询连对挑战最高记录
      this.loadMaxChallengeCount()

      // 查询每日一题坚持天数
      this.loadDailyStreakDays()

      // 确保今日计划已生成，然后加载今日统计和目标
      this.ensureTodayPlan().then(() => {
        this.loadTodayStats().then(() => {
          this.checkReviewStatus()
        })
        this.loadDailyTargetFromProgress()
      })

      // 计划天数
      this.loadDayProgressFromAmcInfo()

      // 本月完成天数
      this.loadFinishedDaysThisMonth()
    },

    // 检查是否需要复习错题
    checkReviewStatus() {
      if (!this.reviewReminderEnabled) return

      if (this.todayStats.wrong > 0 && this.todayStats.wrong > this.todayStats.review) {
        const unreviewed = this.todayStats.wrong - this.todayStats.review
        this.$confirm(`您今日有 ${unreviewed} 道错题尚未复习，建议及时巩固。`, '复习提醒', {
          confirmButtonText: '去复习',
          cancelButtonText: '稍后',
          type: 'warning'
        }).then(() => {
          this.goToTodayWrong()
        }).catch(() => {})
      }
    },

    // 处理复习提醒开关变化
    handleReminderSwitch(val) {
      this.reviewReminderEnabled = val
      localStorage.setItem('amc_review_reminder', String(val))
      if (val) {
        this.$message.success('已开启复习提醒')
        this.checkReviewStatus()
      } else {
        this.$message.info('已关闭复习提醒')
      }
    },

    // 确保今日计划已生成（使用通用接口）
    async ensureTodayPlan() {
      const repoId = this.getAmcRepo()
      if (!repoId) return

      const userInfo = getTokenInfo && getTokenInfo()
      const userId = userInfo ? (userInfo.id ?? userInfo.userId) : null
      if (!userId) return

      try {
        const today = this.getTodayStr()

        // 1. 检查当日进度
        const progressRes = await getProgressByDate(userId, repoId, today)
        if (progressRes?.data) {
          // 已有记录，直接返回
          return
        }

        // 2. 无记录，尝试生成
        const schemeRes = await getSchemeByUserIdAndRepoId(userId, repoId)
        const scheme = schemeRes?.data
        if (scheme && scheme.id) {
          await upsertTodayPlan({ userId, schemeId: scheme.id, repoId, date: today })
          console.log('主页自动生成今日计划成功')
        }
      } catch (e) {
        console.warn('主页自动生成今日计划失败:', e)
      }
    },

    // 根据 amcRepo 统计题库总题数 - 使用通用接口
    async loadRepoQuestions() {
      try {
        const repoId = this.getAmcRepo()
        console.log('[AMCCourse] loadRepoQuestions repoId =', repoId)
        if (!repoId) {
          console.warn('loadRepoQuestions: 缺少 repoId，跳过统计题目数量')
          return
        }
        // 使用通用接口：/course-questions/repo/{repoId}/count
        console.log('[AMCCourse] 请求总题数: /course-questions/repo/', repoId, '/count')
        const res = await countQuestionsByRepoId(repoId)
        console.log('[AMCCourse] 总题数响应 =', res)
        // 通用接口返回：{ code, data: number }
        const total = typeof res?.data === 'number' ? res.data : 0
        this.totalQuestions = total
      } catch (e) {
        console.warn('统计题库题目数量失败:', e)
        this.totalQuestions = 0
      }
    },

    // 查询每日一题坚持天数（当前用户）
    async loadDailyStreakDays() {
      try {
        const userInfo = getTokenInfo && getTokenInfo()
        const userId = userInfo ? userInfo.id : null
        if (!userId) {
          console.warn('[AMCCourse] 缺少 userId，跳过每日一题坚持天数统计')
          return
        }
        console.log('[AMCCourse] 请求每日一题坚持天数: /amc-status/dailyStreakDays?userId=', userId)
        const resp = await getDailyStreakDays(userId)
        console.log('[AMCCourse] 每日一题坚持天数响应 =', resp)
        const val = typeof resp?.data === 'number' ? resp.data : 0
        this.dailyStreak = val || 0
      } catch (e) {
        console.warn('获取每日一题坚持天数失败:', e)
      }
    },

    // 根据API返回的year字段计算考试日期和剩余天数
    calculateExamDateAndDaysLeft(year) {
      try {
        // 创建考试日期：year年1月22日
        const examDate = new Date(year, 0, 22) // 月份从0开始，所以0表示1月
        const today = new Date()

        // 计算剩余天数
        const timeDiff = examDate.getTime() - today.getTime()
        const daysLeft = Math.ceil(timeDiff / (1000 * 3600 * 24))

        // 格式化考试日期显示
        this.examDate = `${year}年01月22日`
        this.daysLeft = Math.max(0, daysLeft) // 确保不显示负数

        console.log(`考试日期: ${this.examDate}, 剩余天数: ${this.daysLeft}`)
      } catch (error) {
        console.error('计算考试日期失败:', error)
        this.examDate = ''
        this.daysLeft = 0
      }
    },

    // 根据选择的级别更新数据（若后端未提供 year，按“下一次考试日”动态计算倒计时）
    updateDataBasedOnLevel() {
      if (!this.isAmcMode()) {
        this.examDate = ''
        this.daysLeft = 0
        return
      }
      const today = new Date()
      const y = today.getFullYear()
      const toDateStr = (d) => `${d.getFullYear()}年${String(d.getMonth() + 1).padStart(2,'0')}月${String(d.getDate()).padStart(2,'0')}日`
      const diffDays = (target) => Math.max(0, Math.ceil((target.getTime() - today.getTime()) / (1000 * 3600 * 24)))

      const pickNext = (monthIndex, day) => {
        // monthIndex: 0-11
        let target = new Date(y, monthIndex, day)
        if (target.getTime() <= today.getTime()) target = new Date(y + 1, monthIndex, day)
        return target
      }

      let target
      if (this.userLevel === 'amc10' || this.userLevel === 'amc12') {
        target = pickNext(1, 5) // 2月5日
      } else {
        target = pickNext(0, 22) // 1月22日
      }
      this.examDate = toDateStr(target)
      this.daysLeft = diffDays(target)
    },

    // 判断某一天是否为本月的已完成日期
    isDateCompleted(dayStr) {
      if (!dayStr || !Array.isArray(this.completedDatesThisMonth)) return false
      return this.completedDatesThisMonth.includes(dayStr)
    },

    // 返回上一页
    goBack() {
      this.$router.go(-1)
    },

    openInNewWindow(routeLocation) {
      const routeData = this.$router.resolve(routeLocation)
      window.open(routeData.href, '_blank')
    },

    async goToCourseResources() {
      if (!(await this.ensureFeatureAccess('AMC_COURSE_RESOURCES'))) return
      // 进入课堂（按当前级别过滤班级列表），会员班级仅对已开通会员的用户可见
      const lvlRaw = this.isAmcMode()
        ? (this.selectedAmcLevel || this.userLevel || localStorage.getItem('selectedAmcLevel') || 'amc8')
        : (this.selectedLevelDisplay || this.userLevel)
      const lvl = String(lvlRaw || '').trim().toUpperCase()
      const classroomQuery = { courseLevel: lvl }
      if (this.amcRepo) classroomQuery.repoId = this.amcRepo
      this.openInNewWindow({ path: '/amc-classroom', query: classroomQuery })
    },

    // 直接进入课程资料（购买检查通过后调用）
    async goToCourseResourcesDirectly() {
      try {
        // 获取当前用户ID
        const tokenInfo = getTokenInfo && getTokenInfo()
        console.log('[AMCCourse] tokenInfo:', tokenInfo)
        const userId = tokenInfo ? (tokenInfo.id ?? tokenInfo.userId) : null
        const username = tokenInfo ? (tokenInfo.username ?? tokenInfo.userName) : null
        console.log('[AMCCourse] userId:', userId, 'username:', username)

        if (!userId) {
          this.$message.error('无法获取用户信息，请重新登录后再试')
          return
        }

        // 统一进入课堂（按当前级别过滤班级列表），避免固定大班导致混入其他课程班级
        const lvlRaw = this.isAmcMode()
          ? (this.selectedAmcLevel || this.userLevel || localStorage.getItem('selectedAmcLevel') || 'amc8')
          : (this.selectedLevelDisplay || '')
        const lvl = String(lvlRaw || '').trim().toUpperCase()
        const limit = getFeatureLimit('AMC_COURSE_RESOURCES', this.isMember)
        const classroomQuery = { courseLevel: lvl }
        if (this.amcRepo) classroomQuery.repoId = this.amcRepo
        if (limit > 0) classroomQuery.limit = limit
        this.openInNewWindow({ path: '/amc-classroom', query: classroomQuery })
      } catch (error) {
        if (error === 'cancel' || (error && error === 'close')) {
          this.$message.info('已取消进入课程')
          return
        }
        console.error('获取课程信息失败:', error)
        this.$message.error('获取课程信息失败，请稍后重试')
      }
    },

    // 快速智能练习
    async startQuickPractice() {
      if (!(await this.ensureFeatureAccess('AMC_QUICK_PRACTICE'))) return
      const amcRepo = this.getAmcRepo()
      if (!amcRepo) {
        this.$message.error('无法获取题库信息，请重新选择AMC级别')
        return
      }
      // 更新用户状态，避免路由守卫拦截
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
        if (userInfo && userInfo.roleId === 1) {
          userInfo.hasSelectedCourses = true
          localStorage.setItem('userInfo', JSON.stringify(userInfo))
        }
      } catch (e) {
        console.error('更新用户状态失败:', e)
      }
      this.confirmQuickPractice()
    },
    async confirmQuickPractice() {
      const repoId = this.getAmcRepo()
      if (!repoId) {
        this.$message.error('无法获取题库信息，请重新选择AMC级别')
        return
      }
      try {
        const userInfo = getTokenInfo && getTokenInfo()
        const userId = userInfo ? (userInfo.id ?? userInfo.userId) : null
        if (!userId) {
          this.$message.error('无法获取用户信息，请重新登录后再试')
          return
        }

        const today = this.getTodayStr()

        // 1) 检查当日进度（使用通用接口）
        const progressRes = await getProgressByDate(userId, repoId, today)
        const progress = progressRes?.data

        if (progress && progress.questionId) {
          const questionIdStr = String(progress.questionId || '').trim()
          const finishedStr = String(progress.finishedId || progress.finished_id || '').trim()
          const allIds = questionIdStr
            ? questionIdStr.split(/[，,;\s]+/).map(s => Number(String(s).trim())).filter(n => !isNaN(n) && n > 0)
            : []
          const finishedIds = finishedStr
            ? finishedStr.split(/[，,;\s]+/).map(s => Number(String(s).trim())).filter(n => !isNaN(n) && n > 0)
            : []
          const finishedSet = new Set(finishedIds)
          const remainingIds = allIds.filter(id => !finishedSet.has(id))

          const plannedTotal = Number(progress.plannedCount ?? progress.planned_count) || allIds.length
          const endless = !remainingIds.length ? '1' : ''

          this.$router.push({
            path: '/quick-practice',
            query: { repoId, plan: questionIdStr, plannedTotal, endless }
          })
          return
        }

        // 2) 无当日记录，按方案生成今日练习（使用通用接口）
        const schemeRes = await getSchemeByUserIdAndRepoId(userId, repoId)
        const scheme = schemeRes?.data
        if (!scheme || !scheme.id) {
          this.$message.error('未找到学习方案，无法生成今日练习')
          return
        }

        const planRes = await upsertTodayPlan({ userId, schemeId: scheme.id, repoId, date: today })
        const planPayload = planRes?.data?.data ?? planRes?.data ?? planRes
        const questionIdStr = planPayload?.questionId || ''
        const allIds = questionIdStr
          ? questionIdStr.split(/[，,;\s]+/).map(s => Number(String(s).trim())).filter(n => !isNaN(n) && n > 0)
          : []
        const plannedTotal = allIds.length
        this.$message.success('已生成今日练习')
        this.$router.push({
          path: '/quick-practice',
          query: { repoId, plan: questionIdStr, plannedTotal }
        })
      } catch (e) {
        this.$message.warning('生成今日练习失败，将直接进入练习')
        this.$router.push({ path: '/quick-practice', query: { repoId } })
      }
    },

    // 知识点精练
    async goToKnowledgePoints() {
      if (!(await this.ensureFeatureAccess('AMC_KNOWLEDGE_POINTS'))) return
      const repoId = this.getAmcRepo()
      const courseTypeCode = this.getEffectiveCourseTypeCode()
      const featureCode = this.resolveFeatureCode('AMC_KNOWLEDGE_POINTS', courseTypeCode)
      const limit = getFeatureLimit(featureCode, this.isMember)
      const query = repoId ? { repoId } : {}
      if (limit > 0) query.limit = limit
      this.$router.push({ path: '/knowledge-practice', query })
    },

    // 每日一题
    goToDailyQuestion() {
      // 获取amcRepo值
      const amcRepo = this.getAmcRepo()
      if (!amcRepo) {
        this.$message.error('无法获取题库信息，请重新选择AMC级别')
        return
      }

      // 跳转到每日一题页面，传递repoId参数
      this.$router.push({
        path: '/daily-question',
        query: {
          repoId: amcRepo
        }
      })
    },

    // 今日错题
    async goToTodayWrong() {
      if (!(await this.ensureFeatureAccess('AMC_TODAY_WRONG'))) return
      const amcRepo = this.getAmcRepo()
      if (!amcRepo) {
        this.$message.error('无法获取题库信息，请重新选择AMC级别')
        return
      }
      this.$router.push({
        path: '/today-wrong',
        query: {
          repoId: amcRepo,
          level: this.selectedLevelDisplay || this.selectedAmcLevel,
          courseTypeId: this.selectedCourseTypeId,
          courseTypeCode: this.selectedCourseTypeCode
        }
      })
    },

    // 连对挑战
    async goToConsecutiveChallenge() {
      if (!(await this.ensureFeatureAccess('AMC_CONSECUTIVE_CHALLENGE'))) return
      // 获取amcRepo值
      const amcRepo = this.getAmcRepo()
      if (!amcRepo) {
        this.$message.error('无法获取题库信息，请重新选择AMC级别')
        return
      }

      // 跳转到连对挑战页面，传递repoId参数
      this.$router.push({
        path: '/consecutive-challenge',
        query: this.buildCourseCenterQuery({ repoId: amcRepo })
      })
    },

    // 历年真题
    async goToPastPapers() {
      if (!(await this.ensureFeatureAccess('AMC_PAST_PAPERS'))) return
      // 获取amcRepo值
      const amcRepo = this.getAmcRepo()
      if (!amcRepo) {
        this.$message.error('无法获取题库信息，请重新选择AMC级别')
        return
      }

      // 获取课程类型
      const courseType = this.getEffectiveCourseTypeCode() || 'AMC'
      const limit = getFeatureLimit('AMC_PAST_PAPERS', this.isMember)

      // 跳转到历年真题页面，传递repoId和courseType参数
      const query = {
          repoId: amcRepo,
          courseType: courseType,
          courseTypeId: this.selectedCourseTypeId || undefined,
          courseTypeCode: this.selectedCourseTypeCode || undefined,
          level: this.selectedLevelDisplay || this.selectedAmcLevel || undefined
        }

      // 提取 GESP 等级：优先从 selectedLevelDisplay，然后从 selectedCourseTypeCode
      if (courseType === 'GESP') {
        const levelSources = [
          this.selectedLevelDisplay,
          this.selectedCourseTypeCode,
          this.currentCourseType?.code,
          this.currentCourseType?.name
        ]
        for (const src of levelSources) {
          const m = String(src || '').trim().toUpperCase().match(/(?:GESP|CCF).*?(?:LEVEL)?\s*([1-8])/)
          if (m && m[1]) {
            query.level = Number(m[1])
            break
          }
        }
      }
      if (limit > 0) query.limit = limit
      this.$router.push({ path: '/amc-past-papers', query })
    },

    // 课程资料
    async goToCourseMaterials() {
      if (!(await this.ensureFeatureAccess('AMC_COURSE_MATERIALS'))) return
      // 获取amcRepo值
      const amcRepo = this.getAmcRepo()
      if (!amcRepo) {
        this.$message.error('无法获取题库信息，请重新选择AMC级别')
        return
      }

      // 获取课程类型
      const courseType = this.getEffectiveCourseTypeCode() || 'AMC'
      const limit = getFeatureLimit('AMC_COURSE_MATERIALS', this.isMember)

      // 跳转到课程资料页面，传递repoId和courseType参数
      const query = {
          repoId: amcRepo,
          courseType: courseType
        }
      if (limit > 0) query.limit = limit
      this.$router.push({ path: '/course-materials', query })
    },

    // 模拟测试
    async goToMockTest() {
      // 获取amcRepo值
      const amcRepo = this.getAmcRepo()
      if (!amcRepo) {
        this.$message.error('无法获取题库信息，请重新选择AMC级别')
        return
      }

      const courseType = this.getEffectiveCourseTypeCode() || 'AMC'
      if (courseType === 'GESP') {
        const query = { repoId: amcRepo }
        // 提取 GESP 等级
        const levelSources = [
          this.selectedLevelDisplay,
          this.selectedCourseTypeCode,
          this.currentCourseType?.code,
          this.currentCourseType?.name
        ]
        for (const src of levelSources) {
          const m = String(src || '').trim().toUpperCase().match(/(?:GESP|CCF).*?(?:LEVEL)?\s*([1-8])/)
          if (m && m[1]) {
            query.level = Number(m[1])
            break
          }
        }
        this.$router.push({ path: '/gesp-mock-test', query })
      } else {
        this.$router.push({
          path: '/amc-mock-test',
          query: { repoId: amcRepo, courseType: courseType }
        })
      }
    },

    // 错题集
    async goToWrongQuestions() {
      if (!(await this.ensureFeatureAccess('AMC_WRONG_BOOK'))) return
      const amcRepo = this.getAmcRepo()
      if (!amcRepo) {
        this.$message.error('无法获取题库信息，请重新选择AMC级别')
        return
      }
      this.$router.push({
        path: '/wrong-questions',
        query: this.buildCourseCenterQuery({ repoId: amcRepo })
      })
    },

    // 知识库
    async goToKnowledgeBase() {
      if (!(await this.ensureFeatureAccess('AMC_KNOWLEDGE_BASE'))) return
      const amcRepo = this.getAmcRepo()
      if (!amcRepo) {
        this.$message.error('无法获取题库信息，请重新选择AMC级别')
        return
      }
      const query = {
        repoId: amcRepo
      }
      if (this.selectedAmcLevel) query.level = this.selectedAmcLevel
      if (!query.level && this.selectedLevelDisplay) query.level = this.selectedLevelDisplay
      if (this.selectedCourseTypeId) query.courseTypeId = this.selectedCourseTypeId
      if (this.selectedCourseTypeCode) query.courseTypeCode = this.selectedCourseTypeCode
      this.$router.push({
        path: '/amc-knowledge',
        query
      })
    },

    // 做题记录
    async goToPracticeRecord() {
      if (!(await this.ensureFeatureAccess('AMC_PRACTICE_RECORD'))) return
      const amcRepo = this.getAmcRepo()
      if (!amcRepo) {
        this.$message.error('无法获取题库信息，请重新选择AMC级别')
        return
      }
      this.$router.push({
        path: '/question-records',
        query: this.buildCourseCenterQuery({ repoId: amcRepo })
      })
    },

    async goToLearningAnalytics() {
      const amcRepo = this.getAmcRepo()
      if (!amcRepo) {
        this.$message.error('无法获取题库信息，请重新选择当前科目')
        return
      }
      const query = this.buildCourseCenterQuery({ repoId: amcRepo })
      try {
        await this.$msgbox({
          title: '学情分析',
          message: '请选择查看学情分析界面，或直接下载包含 AI 学习建议的 PDF 报告。',
          showCancelButton: true,
          distinguishCancelAndClose: true,
          confirmButtonText: '查看',
          cancelButtonText: '下载',
          type: 'info'
        })
        this.$router.push({
          path: '/learning-analytics',
          query
        })
      } catch (action) {
        if (action === 'cancel') {
          await this.startLearningAnalyticsPdfExport(query)
        }
      }
    },
    startAnalyticsExportAnimation() {
      this.stopAnalyticsExportAnimation()
      this.analyticsExportDots = ''
      this.analyticsExportTimer = setInterval(() => {
        this.analyticsExportDots = this.analyticsExportDots.length >= 3 ? '' : `${this.analyticsExportDots}.`
      }, 450)
    },
    stopAnalyticsExportAnimation() {
      if (this.analyticsExportTimer) {
        clearInterval(this.analyticsExportTimer)
        this.analyticsExportTimer = null
      }
      this.analyticsExportDots = ''
    },
    async startLearningAnalyticsPdfExport(query) {
      const userInfo = getTokenInfo && getTokenInfo()
      const userId = userInfo ? (userInfo.id ?? userInfo.userId) : null
      if (!userId) {
        this.$message.error('未获取到用户信息，请重新登录后重试')
        return
      }
      const levelLabel = String(query?.level || query?.courseLevel || query?.courseTypeCode || '当前科目').trim()
      this.analyticsExportFileName = `${levelLabel}-学情分析报告`
      this.analyticsExporting = true
      this.startAnalyticsExportAnimation()
      try {
        const resp = await exportLearningAnalyticsPdf({
          userId,
          repoId: query?.repoId,
          courseType: query?.courseType || query?.courseTypeCode || '',
          level: levelLabel
        })
        const blob = resp?.data instanceof Blob ? resp.data : new Blob([resp?.data || resp], { type: 'application/pdf' })
        if (!blob || !blob.size) {
          throw new Error('学情分析报告文件为空')
        }
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${this.analyticsExportFileName}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        this.handleAnalyticsExportSuccess()
      } catch (error) {
        this.handleAnalyticsExportError(error)
      }
    },
    handleAnalyticsExportSuccess() {
      this.analyticsExporting = false
      this.stopAnalyticsExportAnimation()
      this.$message.success('学情分析 PDF 下载成功')
    },
    handleAnalyticsExportError(error) {
      console.error('导出学情分析PDF失败:', error)
      this.analyticsExporting = false
      this.stopAnalyticsExportAnimation()
      this.$message.error(error?.message || '导出学情分析 PDF 失败，请稍后重试')
    },
    async goToGespCodePractice() {
      if (!(await this.ensureFeatureAccess('AMC_CODE_PRACTICE'))) return
      const repoId = this.getAmcRepo()
      if (!repoId) {
        this.$message.error('无法获取当前课程题库')
        return
      }
      const codeRepoId = this.getCodePracticeRepoId()
      if (!codeRepoId) {
        this.$message.info('当前课程暂未配置编程题练习')
        return
      }
      const query = this.buildCourseCenterQuery({
        source: 'gesp-practice',
        ...(repoId ? { repoId } : {}),
        codeRepoId
      })
      this.$router.push({ path: '/gesp-code-list', query })
    },

    // 重新制定计划
    async recreatePlan() {
      this.$confirm('确定要重新制定学习计划吗？重新制定不会删除旧计划：若选择同级别将覆盖旧计划；若选择不同级别将保留旧计划并新增一条。', '重新制定计划', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          this.$message.success('正在跳转到重新制定学习计划页面...')
          setTimeout(() => {
            this.$router.replace('/amc-level-select?recreate=true')
          }, 300)
        } catch (error) {
          console.error('重置AMC信息失败:', error)
          this.$message.error('重置计划失败，请重试')
        }
      }).catch(() => {
        this.$message.info('已取消重新制定计划')
      })
    },

    // 获取AMC题库ID
    getAmcRepo() {
      // 根据当前选择的级别返回对应的 repoId（参考t_repo表）
      const levelToRepo = {
        'amc8': 14,
        'amc10': 15,
        'amc12': 16,
        '袋鼠数学level1': 17,
        '袋鼠数学level2': 18,
        'gesp level1': 19,
        'ccf-gesp level1': 19,
        'gesp': 19
      }
      const currentLevel = (this.selectedAmcLevel || this.userLevel || localStorage.getItem('selectedAmcLevel') || 'amc8').toLowerCase()

      // 始终根据当前级别计算 repoId，确保切换后数据正确
      const expectedRepo = levelToRepo[currentLevel]
      if (expectedRepo) {
        // 如果缓存的 amcRepo 与当前级别不匹配，更新缓存
        if (this.amcRepo !== expectedRepo) {
          this.amcRepo = expectedRepo
        }
        return this.amcRepo
      }

      // 从AMC数据中获取（兜底）
      if (this.amcData && this.amcData.length > 0) {
        const userData = this.amcData[0]
        if (userData.amcRepo) {
          this.amcRepo = userData.amcRepo
          return this.amcRepo
        }
      }

      // 非 AMC：优先使用已解析的动态题库
      if (this.amcRepo != null) {
        const id = Number(this.amcRepo)
        if (id && !Number.isNaN(id)) return id
      }

      // 如果都没有，返回null
      return null
    }

  }
}
</script>

<style scoped>
.amc-course-container {
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f9fc 0%, #ffffff 100%);
  padding: 0;
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* 顶部导航栏 */
.amc-header {
  background: #fff;
  padding: 15px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.header-right {
  display: flex;
  align-items: center;
}

.avatar-container { margin-right: 0; }
.avatar-container .avatar-wrapper { position: relative; }
.avatar-container .user-avatar { cursor: pointer; width: 36px; height: 36px; border-radius: 50%; }
.avatar-container .el-icon-caret-bottom { position: absolute; right: -16px; top: 22px; font-size: 12px; }

.recreate-plan-btn {
  background: linear-gradient(135deg, #667eea, #764ba2);
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.recreate-plan-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  background: linear-gradient(135deg, #5a6fd8, #6a4190);
}

.recreate-plan-btn:active {
  transform: translateY(0);
}

.close-btn {
  font-size: 20px;
  color: #666;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: background-color 0.3s;
}

.close-btn:hover {
  background-color: #f5f5f5;
}

.course-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.back-btn {
  padding: 0;
  font-size: 16px;
}

.title-dropdown {
  display: flex;
}

.title-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.title-caret {
  color: #909399;
  font-size: 12px;
}

.level-label {
  font-size: 12px;
  color: #606266;
  background: #f2f3f5;
  padding: 2px 8px;
  border-radius: 10px;
}

.amc-home-body {
  padding: 18px 20px 28px;
}

.overview-card,
.task-card,
.feature-panel {
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 8px 30px rgba(31, 45, 61, 0.08);
}

.overview-card {
  padding: 24px 20px 18px;
  margin-bottom: 18px;
}

.overview-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 18px;
}

.overview-stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  cursor: pointer;
}

.overview-stat-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #fff;
  border: 4px solid #f3f5fa;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.03);
}

.overview-stat-icon i {
  font-size: 28px;
  font-weight: 700;
}

.icon-blue i { color: #21b7ff; }
.icon-green i { color: #34c759; }
.icon-red i { color: #ff4d6d; }

.overview-stat-value {
  font-size: 18px;
  line-height: 1.2;
  font-weight: 800;
  color: #111827;
  margin-bottom: 4px;
}

.overview-stat-label {
  font-size: 13px;
  color: #7b8494;
  font-weight: 600;
}

.exam-countdown-line {
  font-size: 18px;
  font-weight: 700;
  color: #646b78;
  text-align: center;
}

.countdown-days {
  font-size: 24px;
  color: #2f90ff;
  margin: 0 6px;
}

.countdown-date {
  margin-left: 6px;
  font-size: 14px;
  color: #8d95a3;
  font-weight: 600;
}

.task-card {
  padding: 22px 22px 8px;
  margin-bottom: 18px;
}

.section-title-main {
  font-size: 20px;
  font-weight: 800;
  color: #121826;
  margin-bottom: 10px;
}

.task-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 64px;
  border-top: 1px solid #f0f2f6;
}

.task-row:first-of-type {
  border-top: none;
}

.task-left {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  font-weight: 700;
  color: #141b28;
}

.task-emoji {
  font-size: 24px;
}

.task-right,
.task-calendar-trigger {
  font-size: 15px;
  color: #8d8d8d;
  font-weight: 700;
}

.quick-entry-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 18px;
}

.quick-entry-card {
  background: linear-gradient(180deg, #ff982f 0%, #ff8a22 100%);
  border-radius: 18px;
  min-height: 176px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 24px rgba(255, 138, 34, 0.28);
  cursor: pointer;
}

.quick-entry-icon {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}

.quick-entry-icon i {
  font-size: 38px;
  color: #fff;
}

.quick-entry-title {
  font-size: 20px;
  font-weight: 800;
  margin-bottom: 8px;
}

.quick-entry-subtitle {
  font-size: 15px;
  font-weight: 600;
  opacity: 0.95;
}

.feature-panel {
  padding: 20px 18px 10px;
}

.feature-panel-collapsible {
  cursor: pointer;
}

.feature-panel-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 48px;
}

.feature-panel-toggle i {
  color: #9aa3b2;
  font-size: 16px;
}

.feature-panel-collapsible:not(.expanded) {
  padding-bottom: 20px;
}

.feature-panel-title {
  text-align: center;
  font-size: 20px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 18px;
}

.feature-menu-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px 10px;
  margin-top: 10px;
}

.feature-menu-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}

.feature-menu-icon {
  width: 82px;
  height: 82px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.1);
}

.feature-menu-icon i {
  color: #fff;
  font-size: 38px;
}

.feature-menu-text {
  font-size: 14px;
  font-weight: 800;
  color: #111827;
  text-align: center;
  line-height: 1.35;
}

.bg-green { background: #34c759; }
.bg-orange { background: #ff982f; }
.bg-red { background: #ff415a; }
.bg-blue { background: #2f90ff; }
.bg-cyan { background: #1fc7d4; }

.feature-menu-icon.bg-orange {
  background: linear-gradient(135deg, #ffb347 0%, #ff8c42 100%);
}

.feature-menu-icon.bg-purple {
  background: linear-gradient(135deg, #7b61ff 0%, #5b8cff 100%);
}

.feature-menu-icon i {
  font-size: 36px;
  color: #fff;
}

.analytics-export-overlay {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}

.analytics-export-dialog {
  width: 420px;
  max-width: calc(100vw - 32px);
  background: #fff;
  border-radius: 20px;
  padding: 28px 24px;
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.18);
  text-align: center;
}

.analytics-export-spinner {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 18px;
}

.analytics-export-spinner span {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2f90ff 0%, #7c4dff 100%);
  animation: analyticsExportPulse 1.2s infinite ease-in-out;
}

.analytics-export-spinner span:nth-child(2) {
  animation-delay: 0.2s;
}

.analytics-export-spinner span:nth-child(3) {
  animation-delay: 0.4s;
}

.analytics-export-title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 10px;
}

.analytics-export-desc {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
}

.analytics-export-host {
  position: fixed;
  left: -20000px;
  top: 0;
  width: 1200px;
  pointer-events: none;
  opacity: 0;
}

@keyframes analyticsExportPulse {
  0%,
  80%,
  100% {
    transform: scale(0.72);
    opacity: 0.45;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .amc-header {
    padding: 10px 15px;
    flex-direction: column;
    gap: 10px;
  }

  .header-left {
    width: 100%;
    justify-content: space-between;
  }

  .header-right {
    width: 100%;
    justify-content: center;
  }

  .course-title {
    font-size: 16px;
  }

  .recreate-plan-btn {
    font-size: 12px;
    padding: 6px 12px;
  }

  .amc-home-body {
    padding: 14px 14px 24px;
  }

  .overview-stats {
    gap: 8px;
  }

  .overview-stat-icon {
    width: 60px;
    height: 60px;
  }

  .overview-stat-icon i {
    font-size: 24px;
  }

  .overview-stat-value {
    font-size: 16px;
  }

  .exam-countdown-line {
    font-size: 16px;
  }

  .countdown-days {
    font-size: 22px;
  }

  .task-card,
  .overview-card,
  .feature-panel {
    border-radius: 20px;
  }

  .quick-entry-grid {
    gap: 12px;
  }

  .quick-entry-card {
    min-height: 152px;
  }

  .feature-menu-grid {
    gap: 18px 8px;
  }

  .feature-menu-icon {
    width: 72px;
    height: 72px;
  }

  .feature-menu-icon i {
    font-size: 32px;
  }

  .feature-menu-text {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .overview-card,
  .task-card,
  .feature-panel {
    padding-left: 14px;
    padding-right: 14px;
  }

  .quick-entry-card {
    min-height: 138px;
  }

  .quick-entry-title {
    font-size: 18px;
  }

  .quick-entry-subtitle {
    font-size: 13px;
  }
}
</style>

<style>
.amc-month-calendar-popover {
  padding: 8px 12px;
}

.amc-month-calendar-popover .el-calendar {
  width: 420px;
}

.amc-month-calendar-popover .amc-calendar-date {
  position: relative;
  width: 100%;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.amc-month-calendar-popover .amc-calendar-date.is-completed {
  background: #409eff;
  color: #fff;
  border-radius: 16px;
}
</style>
